import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { consentTileStyles } from './MsDefenderIntegration.styles';
import {
  calculateResponseActionHealth,
  formatResponseActionDescription,
  formatResponseActionSuccessDate,
  formatResponseActionTitle,
  getLatestFailedAction
} from './MsDefenderIntegration.utils';
import type { ResponseActionHealthProps } from './ResponseActionHealth.types';

export const ResponseActionHealth = ({
  responseActions,
  onViewHistory
}: ResponseActionHealthProps) => {
  const healthStats = useMemo(
    () => calculateResponseActionHealth(responseActions),
    [responseActions]
  );

  const latestFailedAction = useMemo(
    () => getLatestFailedAction(responseActions),
    [responseActions]
  );

  return (
    <Paper elevation={2} variant="outlined" sx={{ p: 2, mt: 1, ml: 4 }}>
      <Stack
        sx={{
          gap: 0.5
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: 'text.secondary',
            textTransform: 'uppercase'
          }}
        >
          Response Action Health · Last 30 Days
        </Typography>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            pb: 1
          }}
        >
          <Stack
            direction="row"
            onClick={healthStats.total > 0 ? onViewHistory : undefined}
            sx={{
              alignItems: 'baseline',
              gap: 0.5,
              ...consentTileStyles.clickableStat(healthStats.total > 0)
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
              {healthStats.total}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Attempts
            </Typography>
          </Stack>
          <Typography sx={{ color: 'divider', userSelect: 'none' }}>
            |
          </Typography>
          <Stack
            direction="row"
            onClick={healthStats.succeeded > 0 ? onViewHistory : undefined}
            sx={{
              alignItems: 'baseline',
              gap: 0.5,
              ...consentTileStyles.clickableStat(healthStats.succeeded > 0)
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.125rem',
                color: 'success.main'
              }}
            >
              {healthStats.succeeded}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Succeeded
            </Typography>
          </Stack>
          <Typography sx={{ color: 'divider', userSelect: 'none' }}>
            |
          </Typography>
          <Stack
            direction="row"
            onClick={healthStats.failed > 0 ? onViewHistory : undefined}
            sx={{
              alignItems: 'baseline',
              gap: 0.5,
              ...consentTileStyles.clickableStat(healthStats.failed > 0)
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.125rem',
                color: healthStats.failed > 0 ? 'warning.main' : 'text.primary'
              }}
            >
              {healthStats.failed}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Failed
            </Typography>
          </Stack>
          <Typography sx={{ color: 'divider', userSelect: 'none' }}>
            |
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last success{' '}
            {formatResponseActionSuccessDate(healthStats.lastSuccessDate)}
          </Typography>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={onViewHistory}
            endIcon={
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{
                  fontSize: '0.75rem',
                  marginLeft: '-0.25rem'
                }}
              />
            }
            sx={{ ...consentTileStyles.viewHistoryButton, ml: 'auto' }}
          >
            View action history
          </Button>
        </Stack>

        {latestFailedAction && (
          <Alert
            severity={
              latestFailedAction.current_status.toLowerCase() === 'error'
                ? 'error'
                : 'warning'
            }
            icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
            sx={consentTileStyles.recentIssueAlert}
          >
            <Stack>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  textTransform: 'uppercase'
                }}
              >
                Recent Action Issue
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {formatResponseActionTitle(latestFailedAction.action_type)}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {formatResponseActionDescription(latestFailedAction)}
              </Typography>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};
